import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../api/client";
import { qk } from "../../api/queries";
import type { Project, TaskDetail } from "../../api/types";
import { skipChoice } from "../../lib/skip";
import { useDialogs } from "../Dialogs";
import { Icon } from "../Icon";

type ActionKey = "submit" | "draft" | "pause" | "resume" | "cancel" | "retry" | "done";

export function TaskActions({ task, project }: { task: TaskDetail; project: Project | undefined }) {
  const qc = useQueryClient();
  const { confirm, prompt } = useDialogs();
  const stage = task.stages.find((s) => s.key === task.stage_key);
  const s = task.status;
  const closed = s === "done" || s === "cancelled";
  const skip = skipChoice(task);
  const [submitTo, setSubmitTo] = useState("");

  const act = useMutation({
    mutationFn: async ({ key, message }: { key: ActionKey; message?: string }) => {
      switch (key) {
        case "submit":
          return api.submitTask(task.id, submitTo || undefined);
        case "draft":
          return api.draftTask(task.id, message);
        case "pause":
          return api.pauseTask(task.id);
        case "resume":
          return api.resumeTask(task.id);
        case "cancel":
          return api.cancelTask(task.id);
        case "retry":
          return api.retryTask(task.id);
        case "done":
          return api.markDone(task.id);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.task(task.id) });
      qc.invalidateQueries({ queryKey: qk.tasks(task.project_id) });
    },
  });

  const run = (key: ActionKey, message?: string) => act.mutate({ key, message });
  const busy = act.isPending;

  const isHumanStage = stage?.kind === "human";
  const canSubmit = isHumanStage && !closed && !["working", "human_review", "ai_review", "paused"].includes(s);
  const canDraft = isHumanStage && !closed && !["working", "paused"].includes(s);
  const canPause = !closed && s !== "paused";
  const canResume = s === "paused";
  const canRetry = s === "needs_attention";
  // Only an Integration stage waiting for a manual merge (no GitHub) can be marked done.
  const canMarkDone = stage?.kind === "integration" && s === "ready" && !!project && !project.github_repo;

  return (
    <div className="task-actions">
      {canSubmit && (
        <span className="submit-group">
          <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => run("submit")}>
            <Icon name="check" size={14} /> Submit
          </button>
          {skip.targets.length > 0 && (
            <label className="send-back">
              <span className="muted small">to</span>
              <select
                className="input input-inline"
                aria-label="Submit to stage"
                value={submitTo}
                onChange={(e) => setSubmitTo(e.target.value)}
              >
                <option value="">{skip.continueLabel}</option>
                {skip.targets.map((st) => (
                  <option key={st.key} value={st.key}>
                    Skip to {st.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </span>
      )}
      {canDraft && (
        <button
          className="btn btn-sm"
          disabled={busy}
          onClick={async () => {
            const msg = await prompt({
              title: "Draft with agent",
              body: (
                <p className="muted">
                  An agent will refine the title, description, and acceptance criteria. The exchange is recorded in
                  the timeline.
                </p>
              ),
              label: "Instructions (optional)",
              placeholder: "e.g. Turn this into testable acceptance criteria",
              multiline: true,
              confirmLabel: "Start drafting",
            });
            if (msg !== null) run("draft", msg.trim() || undefined);
          }}
        >
          <Icon name="bot" size={14} /> Draft with agent
        </button>
      )}
      {canRetry && (
        <button className="btn btn-sm" disabled={busy} onClick={() => run("retry")}>
          <Icon name="refresh" size={14} /> Retry
        </button>
      )}
      {canResume && (
        <button className="btn btn-sm" disabled={busy} onClick={() => run("resume")}>
          <Icon name="play" size={14} /> Resume
        </button>
      )}
      {canPause && (
        <button className="btn btn-sm" disabled={busy} onClick={() => run("pause")}>
          <Icon name="pause" size={14} /> Pause
        </button>
      )}
      {canMarkDone && (
        <button
          className="btn btn-sm"
          disabled={busy}
          onClick={async () => {
            if (await confirm({ title: `Mark #${task.number} done?`, confirmLabel: "Mark done" })) run("done");
          }}
        >
          <Icon name="check" size={14} /> Mark done
        </button>
      )}
      {!closed && (
        <button
          className="btn btn-sm btn-ghost-danger"
          disabled={busy}
          onClick={async () => {
            const ok = await confirm({
              title: `Cancel #${task.number}?`,
              body: <p>Any running agent is stopped and the environment is scheduled for teardown.</p>,
              confirmLabel: "Cancel task",
              danger: true,
            });
            if (ok) run("cancel");
          }}
        >
          <Icon name="x" size={14} /> Cancel
        </button>
      )}
    </div>
  );
}
